import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, Save } from "lucide-react";
import { toast } from "sonner";

export function GlobalR2ConfigForm() {
  const [form, setForm] = useState({
    r2AccessKeyId: "",
    r2SecretAccessKey: "",
    r2Endpoint: "",
    r2BucketName: "",
  });

  // Query to get current global R2 settings
  const { data: settings, refetch } = trpc.settings.getGlobalR2.useQuery(undefined, {
    retry: false,
    onError: () => {
      // Settings might not exist yet, that's okay
    },
  });

  // Mutation to update global R2 settings
  const updateMutation = trpc.settings.updateGlobalR2.useMutation({
    onSuccess: () => {
      toast.success("Global R2 configuration updated successfully");
      refetch();
      // Clear secret field after successful save
      setForm(prev => ({ ...prev, r2SecretAccessKey: "" }));
    },
    onError: (error: any) => {
      toast.error(`Failed to update R2 configuration: ${error.message}`);
    },
  });

  useEffect(() => {
    if (settings) {
      setForm({
        r2AccessKeyId: settings.r2AccessKeyId || "",
        r2SecretAccessKey: "", // Never populate secret
        r2Endpoint: settings.r2Endpoint || "",
        r2BucketName: settings.r2BucketName || "",
      });
    }
  }, [settings]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!form.r2AccessKeyId || !form.r2Endpoint || !form.r2BucketName) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Secret is required only if not already set
    if (!settings?.r2SecretSet && !form.r2SecretAccessKey) {
      toast.error("Secret Access Key is required for initial setup");
      return;
    }

    updateMutation.mutate({
      r2AccessKeyId: form.r2AccessKeyId.trim(),
      r2SecretAccessKey: form.r2SecretAccessKey.trim() || undefined,
      r2Endpoint: form.r2Endpoint.trim(),
      r2BucketName: form.r2BucketName.trim(),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Global R2 Storage Configuration</CardTitle>
        <CardDescription>
          Configure the shared Cloudflare R2 storage for all users
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertDescription>
            This is a global configuration. All users will use this R2 bucket with individual folders for data isolation (users/&#123;userId&#125;/).
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="r2AccessKeyId">
              Access Key ID <span className="text-destructive">*</span>
            </Label>
            <Input
              id="r2AccessKeyId"
              value={form.r2AccessKeyId}
              onChange={(e) => setForm({ ...form, r2AccessKeyId: e.target.value })}
              placeholder="Enter R2 Access Key ID"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="r2SecretAccessKey">
              Secret Access Key {settings?.r2SecretSet ? "" : <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="r2SecretAccessKey"
              type="password"
              value={form.r2SecretAccessKey}
              onChange={(e) => setForm({ ...form, r2SecretAccessKey: e.target.value })}
              placeholder={settings?.r2SecretSet ? "••••••••" : "Enter R2 Secret Access Key"}
            />
            {settings?.r2SecretSet && (
              <p className="text-sm text-muted-foreground">
                Leave empty to keep existing secret
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="r2Endpoint">
              Endpoint <span className="text-destructive">*</span>
            </Label>
            <Input
              id="r2Endpoint"
              value={form.r2Endpoint}
              onChange={(e) => setForm({ ...form, r2Endpoint: e.target.value })}
              placeholder="e.g., abc123.r2.cloudflarestorage.com"
              required
            />
            <p className="text-sm text-muted-foreground">
              Your R2 endpoint URL (without https://)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="r2BucketName">
              Bucket Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="r2BucketName"
              value={form.r2BucketName}
              onChange={(e) => setForm({ ...form, r2BucketName: e.target.value })}
              placeholder="e.g., okulus-unified-storage"
              required
            />
          </div>

          <Button 
            type="submit" 
            disabled={updateMutation.isPending}
            className="w-full"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? "Saving..." : "Save Configuration"}
          </Button>
        </form>

        {settings && (
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h4 className="font-semibold mb-2">Current Status</h4>
            <div className="space-y-1 text-sm">
              <p>
                <span className="font-medium">Access Key:</span>{" "}
                {settings.r2AccessKeyId ? "Configured" : "Not set"}
              </p>
              <p>
                <span className="font-medium">Secret Key:</span>{" "}
                {settings.r2SecretSet ? "Configured" : "Not set"}
              </p>
              <p>
                <span className="font-medium">Endpoint:</span>{" "}
                {settings.r2Endpoint || "Not set"}
              </p>
              <p>
                <span className="font-medium">Bucket:</span>{" "}
                {settings.r2BucketName || "Not set"}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}